import { IntegratedFreightShippingData } from './IntegratedFreightShippingFragment.props';
import {
  ArchiveFreightOrderType,
  CreateFreightOrderType,
  DispatchDetail,
  QuoteFreightOrderTypeResponse,
  ReturnFreightOrderType,
} from '../../../../../@types/shipment.type';
import { DropdownItem } from '../../../../../components/atoms/SelectMenu';
import { SelectedShipmentPackage } from '../../../../../components/molecules/ShipmentPackagesList';
import { AddressDataType } from '../../../../../components/organisms/StructureAddress';
import {
  CountryCode,
  FreightIntegrator,
  FreightOrderReference,
  NodeAttributeType,
} from '../../../../../configs/enums';
import { checkIntegratedFreightErrors } from '../../../../../utils/shipment';
import { toast } from '../../../../../utils/toast';

interface OrdersDataShape {
  mainId?: number;
  mainOrderId?: string;
  mainOrderNumber?: string;
  returnedId?: number;
  returnedOrderId?: string;
  returnedOrderNumber?: string;
}

export interface BuildFreightActionsArgs {
  shipment: DispatchDetail;
  data: IntegratedFreightShippingData;
  serviceTypes: DropdownItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; contact attribute shape is untyped at source.
  contactData: any[] | undefined;
  destinationAddress: AddressDataType | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; destination address data shape is untyped at the source.
  destinationAddressdata: any[] | undefined;
  stateShortName?: string;
  ordersData: OrdersDataShape;
  createFreightOrder: (payload: CreateFreightOrderType) => Promise<unknown>;
  archiveFreightOrder: (payload: ArchiveFreightOrderType) => Promise<unknown>;
  returnFreightOrder: (payload: ReturnFreightOrderType) => Promise<unknown>;
  quoteFreightOrder: (payload: unknown) => Promise<QuoteFreightOrderTypeResponse[]>;
  signalFreightAction: () => void;
  setQuoteData: (data: QuoteFreightOrderTypeResponse[]) => void;
  setOpenModal: (v: boolean) => void;
  onPackageSelect: (packages: SelectedShipmentPackage[]) => void;
}

export const buildFreightActions = ({
  shipment,
  data,
  serviceTypes,
  contactData,
  destinationAddress,
  destinationAddressdata,
  stateShortName,
  ordersData,
  createFreightOrder,
  archiveFreightOrder,
  returnFreightOrder,
  quoteFreightOrder,
  signalFreightAction,
  setQuoteData,
  setOpenModal,
  onPackageSelect,
}: BuildFreightActionsArgs) => {
  const buildPackagesList = () => {
    const out: { weight: number; height: number; width: number; length: number }[] = [];
    data.selectedPackages.forEach((value) => {
      for (let i = 0; i < value.quantity; i++) {
        out.push({
          weight: value.weight,
          height: value.height / 100,
          width: value.width / 100,
          length: value.length / 100,
        });
      }
    });
    return out;
  };

  const reportError = (fallback: string) => (error: unknown) => {
    const message = error instanceof Error && error.message ? error.message : fallback;
    toast.error(message);
    signalFreightAction();
  };

  const onSaveOrder = async () => {
    if (!shipment.id) {
      toast.warning('Cannot create freight order: shipment must be saved first');
      signalFreightAction();
      return;
    }

    const shippingMethod = serviceTypes.find((item) => item.value === data.serviceType)?.value;
    const phoneNumber = contactData?.find(
      (item) => item.propertyTypeCode === NodeAttributeType.Contact
    )?.value.phone1;
    const stateCode = stateShortName || destinationAddress?.stateName;
    const destinationNodeName = destinationAddressdata?.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing; destination address shape is untyped.
      (address: any) => address.nodeId === shipment?.destinationNodeId
    )?.node?.name;

    const validateErrors = checkIntegratedFreightErrors(
      'saveOrder',
      destinationAddress as AddressDataType,
      data.serviceType,
      stateCode,
      destinationNodeName
    );
    if (validateErrors !== '') {
      toast.warning(validateErrors);
      signalFreightAction();
      return;
    }

    const payload: CreateFreightOrderType = {
      shipmentId: shipment.id,
      reference: FreightOrderReference.OnlineOrder,
      shipping_method: shippingMethod || '',
      shippingProviderId: data.shippingProviderId,
      signature_required: !data.isAuthToLeave,
      dangerous_goods: data.isSafeDrop,
      destination: {
        name: destinationAddress?.name || destinationAddress?.placeName || destinationNodeName,
        phone: destinationAddress?.contactNumber || phoneNumber || '',
        street: `${destinationAddress?.line1}${destinationAddress?.line2 ? ', ' + destinationAddress?.line2 : ''}`,
        suburb: destinationAddress?.suburb,
        city: destinationAddress?.city || destinationAddress?.suburb,
        state: stateCode,
        post_code: destinationAddress?.postCode,
        country: destinationAddress?.countryName,
        delivery_instructions: data.deliveryNote,
        company: destinationAddress?.contactCompany || '',
      },
      packages: buildPackagesList(),
    };

    try {
      await createFreightOrder(payload);
      toast.success('Freight order created');
      signalFreightAction();
    } catch (error) {
      reportError('Failed to create freight order')(error);
    }
  };

  const onQuote = async () => {
    const validateErrors = checkIntegratedFreightErrors(
      'quote',
      destinationAddress as AddressDataType,
      '',
      stateShortName
    );
    if (validateErrors !== '') {
      toast.warning(validateErrors);
      signalFreightAction();
      return;
    }

    try {
      const quoteOrder = await quoteFreightOrder({
        street: `${destinationAddress?.line1}${destinationAddress?.line2 ? ', ' + destinationAddress?.line2 : ''}`,
        suburb: destinationAddress?.suburb,
        city: destinationAddress?.city || destinationAddress?.suburb,
        state: stateShortName,
        post_code: destinationAddress?.postCode?.toString(),
        country_code: CountryCode.Australia,
        packages: buildPackagesList(),
        return_order: false,
      });

      if (Array.isArray(quoteOrder) && quoteOrder.length > 0) {
        setQuoteData(quoteOrder);
        setOpenModal(true);
      }
    } catch (error) {
      reportError('Failed to fetch freight quote')(error);
    }
  };

  const onArchiveOrder = async () => {
    if (!ordersData.mainId) {
      toast.warning('No freight order to archive');
      signalFreightAction();
      return;
    }
    try {
      await archiveFreightOrder({
        id: ordersData.mainId,
        shipmentId: shipment.id,
        freightIntegrator: FreightIntegrator.StarShipIt,
        orderNumber: ordersData.mainOrderNumber,
        orderId: ordersData.mainOrderId,
        isReturnOrder: false,
      });
      toast.success('Freight order archived');
      signalFreightAction();
    } catch (error) {
      reportError('Failed to archive freight order')(error);
    }
  };

  const onArchiveReturnOrder = async () => {
    if (!ordersData.returnedId) {
      toast.warning('No return order to archive');
      signalFreightAction();
      return;
    }
    try {
      await archiveFreightOrder({
        id: ordersData.returnedId,
        shipmentId: shipment.id,
        freightIntegrator: FreightIntegrator.StarShipIt,
        orderNumber: ordersData.returnedOrderNumber,
        orderId: ordersData.returnedOrderId,
        isReturnOrder: true,
      });
      toast.success('Return order archived');
      signalFreightAction();
    } catch (error) {
      reportError('Failed to archive return order')(error);
    }
  };

  const onReturnOrder = async () => {
    if (!ordersData.mainId) {
      toast.warning('Cannot create return order: original freight order not found');
      signalFreightAction();
      return;
    }
    try {
      await returnFreightOrder({
        shipmentId: shipment.id,
        freightIntegrator: FreightIntegrator.StarShipIt,
        orderNumber: ordersData.mainOrderNumber,
        orderId: ordersData.mainOrderId,
      });
      toast.success('Return order created');
      signalFreightAction();
    } catch (error) {
      reportError('Failed to create return order')(error);
    }
  };

  return {
    onPackageSelect,
    onSaveOrder,
    onQuote,
    onArchiveOrder,
    onArchiveReturnOrder,
    onReturnOrder,
  };
};
